// docs/116 CI6/CI12: the inn's moments (toasts, drinks slid down the bar, songs asked for) arrive as
// `inn_cheer` event rows, broadcast and gone. While the inn is on screen this subscribes to them and
// keeps the last few that were for this village.
import { useEffect, useRef, useState } from 'react';
import { tables } from '../../bindings';
import { getConn, identityHex, tsToMs } from '../net/connection';
import { serverNow } from '../time';
import type { EventContext } from '../../bindings';

export interface CheerRow { worldId: bigint; villageId: bigint; owner: { toHexString(): string }; kind: number; heroId: bigint; arg: number; seed: number }
export interface Cheer extends Omit<CheerRow, 'owner'> { n: number; owner: string }

export function useInnCheers(worldId: bigint | undefined, villageId: bigint | undefined, on: boolean): Cheer[] {
  const [rows, setRows] = useState<Cheer[]>([]);
  const n = useRef(0);
  useEffect(() => {
    const c = getConn();
    if (!on || !c || worldId === undefined || villageId === undefined) return;
    const sub = c.subscriptionBuilder()
      .onError((_ctx, err?: Error) => console.error('[inn] subscription error', err))
      .subscribe([tables.innCheer.where((r) => r.worldId.eq(worldId))]);
    const onRow = (_ctx: EventContext, row: CheerRow) => {
      if (row.worldId !== worldId || row.villageId !== villageId) return;
      setRows((rs) => [...rs.slice(-7), { ...row, owner: identityHex(row.owner as never), n: ++n.current }]);
    };
    c.db.innCheer.onInsert(onRow);
    return () => { c.db.innCheer.removeOnInsert(onRow); sub.unsubscribe(); };
  }, [worldId, villageId, on]);
  return rows;
}

// docs/121 VW2: the moments a player starts in the inn that every view plays alike (`village_moment`):
// petting the cat, a game of fetch. Same as the cheers: subscribed while the inn is on screen, the last
// few kept, each with the module's seed and when it happened.
export interface MomentRow { worldId: bigint; villageId: bigint; spot: number; owner: { toHexString(): string }; kind: number; heroId: bigint; x: number; y: number; seed: number; at: { toMillis?(): bigint; microsSinceUnixEpoch?: bigint } }
export interface Moment { n: number; owner: string; kind: number; heroId: bigint; x: number; y: number; seed: number; atMs: number }

export function useVillageMoments(worldId: bigint | undefined, villageId: bigint | undefined, on: boolean): Moment[] {
  const [rows, setRows] = useState<Moment[]>([]);
  const n = useRef(0);
  useEffect(() => {
    const c = getConn();
    if (!on || !c || worldId === undefined || villageId === undefined) return;
    const sub = c.subscriptionBuilder()
      .onError((_ctx, err?: Error) => console.error('[inn] subscription error', err))
      .subscribe([tables.villageMoment.where((r) => r.worldId.eq(worldId))]);
    const onRow = (_ctx: EventContext, row: MomentRow) => {
      if (row.worldId !== worldId || row.villageId !== villageId) return;
      setRows((rs) => [...rs.slice(-7), { n: ++n.current, owner: identityHex(row.owner as never), kind: row.kind, heroId: row.heroId, x: row.x, y: row.y, seed: row.seed, atMs: tsToMs(row.at as never) ?? serverNow() }]);
    };
    c.db.villageMoment.onInsert(onRow);
    return () => { c.db.villageMoment.removeOnInsert(onRow); sub.unsubscribe(); };
  }, [worldId, villageId, on]);
  return rows;
}
